import React, {
  useCallback, useEffect, useState, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useLocation, Outlet } from 'react-router';
import DragAndDropProvider from '../../components/drag-and-drop-provider/drag-and-drop-provider.component';
import ThemeProvider from '../../components/theme-provider/theme-provider.component';
import SecurityLayout from '../../components/security-layout/security-layout.component';
import GlobalStylesBlock from '../../components/global-styles-block/global-styles-block.component';
import Loader from '../../components/loader/loader.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import FlexContainer from '../../components/flex-container/flex-container.component';
import MainContainer from '../../components/main-container/main-container.component';
import MainTheme from '../../themes/main-theme';
import Header from './header/header.component';
import Navigation from './navigation/navigation.component';
import RightDrawer from './right-drawer/right-drawer.component';
import MainService from '../../services/main-service';
import ClientService from '../../services/client-service';
import ScreenService from '../../services/screen-service';
import AuthService from '../../services/auth-service';
import OktaService from '../../services/okta-service';
import IntercomService from '../../services/intercom-service';
import GoogleTagManagerService from '../../services/google-tag-manager-service';
import PushNotificationService from '../../services/push-notification-service';
import LocalStorageService from '../../services/local-storage-service';
import FormatService from '../../services/format-service';
import useCapacitorAppUrlOpen from '../../hooks/use-capacitor-app-url-open';
import {
  getBaseData, resetBaseData, resetAppLoader, userSessionExpired, saveUserPushNotificationToken,
} from './app.action';
import { getCommentListData, commentAdded } from './comments/comments.action';
import { getNotificationListData, notificationAdded } from './notifications/notifications.action';
import { ROUTE_LOGIN } from '../../constants/system/routes';
import { QUERY_PARAM_PORTFOLIO_GROUP_IDS, QUERY_PARAM_PORTFOLIO_IDS } from '../../constants/system/query-params';
import { HTTP_CODE_UNAUTHORIZED } from '../../constants/system/http-codes';
import { NODE_ENV_PRODUCTION } from '../../constants/system/node-envs';
import { SIGN_DOT } from '../../constants/system/signs';

const App: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const STATIC_FILE_PATH_SCRIPT_INTERCOM: string = '/scripts/intercom.min.js';
  const STATIC_FILE_PATH_SCRIPT_GOOGLE_TAG_MANAGER: string = '/scripts/google-tag-manager.min.js';
  const CHECK_CONNECTION_RETRY_LIMIT: number = 15;
  const CHECK_CONNECTION_INTERVAL: number = 5000;

  const {
    isLoading,
    isDataLoaded,
    isEditMode,
    isServerFault,
    isUserSessionExpired,
    currentUser,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    notificationActiveSubscription,
    commentActiveSubscription,
    userSessionExpiredActiveSubscription,
  } = props;

  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();
  const dispatch: ReduxDispatch = useDispatch();

  const initialPathname: string = location.pathname;
  const storedAuthToken: string | null = AuthService.getAuthToken();
  const googleTagManagerId: string | undefined = MainService.GOOGLE_TAG_MANAGER_ID;
  const intercomAppId: string | undefined = MainService.INTERCOM_APP_ID;
  const isGoogleTagManagerEnabled: boolean = MainService.NODE_ENV === NODE_ENV_PRODUCTION;
  const isIntercomEnabled: boolean = MainService.NODE_ENV === NODE_ENV_PRODUCTION;
  const currentPushNotificationToken: string | null = LocalStorageService.getPushNotificationToken();
  const currentUserId: number | null = currentUser ? currentUser.id : null;
  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryFilterPortfolioIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_IDS].toString().split(SIGN_DOT)
        .map((portfolioId: string): number => FormatService.parseInt(portfolioId)) : null
  );
  const queryFilterPortfolioGroupIds: number[] | null = (
    locationQueryParams && locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS]
      ? locationQueryParams[QUERY_PARAM_PORTFOLIO_GROUP_IDS].toString().split(SIGN_DOT)
        .map((portfolioGroupId: string): number => FormatService.parseInt(portfolioGroupId)) : null
  );

  const [currentPathname, setCurrentPathname]: [string, Function] = useState(initialPathname);
  const [appAuthToken, setAppAuthToken]: [string | null, Function] = useState(null);
  const [isGtmClientInitialized, setIsGtmClientInitialized]: [boolean, Function] = useState(false);
  const [isIntercomClientInitialized, setIsIntercomClientInitialized]: [boolean, Function] = useState(false);
  const [isUserSessionConfirmModalOpen, setIsUserSessionConfirmModalOpen]: [boolean, Function] = useState(false);
  const [checkConnectionInterval, setCheckConnectionInterval]: [number | null, Function] = useState(null);

  const storedOktaAccountName: string | null = LocalStorageService.getOktaAccountName();
  const storedOktaAccountClientId: string | null = LocalStorageService.getOktaAccountClientId();
  const oktaAuth: OktaAuth | null = storedOktaAccountName && storedOktaAccountClientId
    ? OktaService.getClient(storedOktaAccountName, storedOktaAccountClientId) : null;
  const userSessionConfirmModalTitle: string = 'Session Expired';
  const userSessionConfirmModalText: string = 'Your session has timed out. Please login again';
  const userSessionConfirmModalConfirmText: string = 'Login';

  const onUserSessionExpired: Function = useCallback((): void => {
    AuthService.deleteAuthToken();
    ClientService.resetToken();
    ClientService.closeConnections();
  }, []);

  const onUserSessionCloseConfirm: Function = useCallback((): void => {
    setIsUserSessionConfirmModalOpen(false);
    onUserSessionExpired();
    dispatch(resetBaseData());

    navigate({
      pathname: ROUTE_LOGIN,
    });
  }, []);

  const closeConfirmModal: Function = useCallback((): void => {
    setIsUserSessionConfirmModalOpen(false);
  }, []);

  const getOptions: Function = useCallback((): RequestOptions => {
    let options: RequestOptions = {};

    options = portfolioIdsFilter && portfolioIdsFilter.length
      ? { ...options, portfolioIds: portfolioIdsFilter } : options;
    options = portfolioGroupIdsFilter && portfolioGroupIdsFilter.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsFilter } : options;

    return options;
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  const initSubscriptions: Function = useCallback((): void => {
    dispatch(notificationAdded({
      userId: currentUserId,
      portfolioIds: portfolioIdsFilter || queryFilterPortfolioIds,
      portfolioGroupIds: portfolioGroupIdsFilter || queryFilterPortfolioGroupIds,
    }));

    dispatch(commentAdded({
      userId: currentUserId,
      portfolioIds: portfolioIdsFilter || queryFilterPortfolioIds,
      portfolioGroupIds: portfolioGroupIdsFilter || queryFilterPortfolioGroupIds,
    }));

    dispatch(userSessionExpired({
      userId: currentUserId,
    }));
  }, [
    currentUserId, portfolioIdsFilter, portfolioGroupIdsFilter, queryFilterPortfolioIds, queryFilterPortfolioGroupIds,
  ]);

  const onOktaAuthRequired: Function = useCallback((): void => {
    navigate({
      pathname: ROUTE_LOGIN,
    });
  }, []);

  const oktaRestoreOriginalUri: Function = useCallback(async (
    oktaAuthParam: OktaAuth, originalUri: string,
  ): Promise<void> => {
    await OktaService.restoreOriginalUri(originalUri, (relativeOriginalUrl: string): void => {
      navigate(relativeOriginalUrl, {
        replace: true,
      });
    });
  }, []);

  const onRegisterMobilePushNotifications: (token: string) => void = useCallback(
    async (token: string): Promise<void> => {
      if (token) {
        dispatch(saveUserPushNotificationToken({
          token,
        }));
      }
    },
    [currentPushNotificationToken],
  );

  const onMobilePushNotificationActionPerformed: ((pushNotificationData: Record<string, StructureItem>) => void) = (
    useCallback(async (pushNotificationData: Record<string, StructureItem>): Promise<void> => {
      if (pushNotificationData && pushNotificationData.link) {
        navigate(pushNotificationData.link);
      }
    }, [])
  );

  const enableMobilePushNotifications: Function = useCallback(async (): Promise<void> => {
    await PushNotificationService.enablePushNotifications(
      onRegisterMobilePushNotifications,
      onMobilePushNotificationActionPerformed,
    );
  }, [currentPushNotificationToken]);

  const reloadApp: Function = useCallback(async (): Promise<void> => {
    if (!checkConnectionInterval) {
      let retryIndex: number = 0;
      const checkConnectionIntervalId: IntervalId = setInterval((): void => {
        dispatch(getBaseData(getOptions()));

        if (checkConnectionIntervalId) {
          if (!isServerFault || retryIndex >= CHECK_CONNECTION_RETRY_LIMIT) {
            clearInterval(checkConnectionIntervalId);
            setCheckConnectionInterval(null);
          }

          if (retryIndex >= CHECK_CONNECTION_RETRY_LIMIT) {
            navigate({
              pathname: ROUTE_LOGIN,
            });
          }
        }
        retryIndex++;
      }, CHECK_CONNECTION_INTERVAL);

      setCheckConnectionInterval(checkConnectionIntervalId);
    }
  }, [checkConnectionInterval, isServerFault, portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void | ReactUseEffectCleanUp => {
    if (isIntercomClientInitialized) {
      const intercomHtmlElementScript: HTMLScriptElement | null = isIntercomEnabled && !!intercomAppId
        ? ScreenService.addHtmlScript(STATIC_FILE_PATH_SCRIPT_INTERCOM, false) : null;

      return (): void => {
        if (intercomHtmlElementScript) {
          ScreenService.removeHeadElement(intercomHtmlElementScript);
        }
      };
    }
  }, [isIntercomClientInitialized]);

  useEffect((): void | ReactUseEffectCleanUp => {
    if (isGtmClientInitialized) {
      const gtmHtmlElementScript: HTMLScriptElement | null = isGoogleTagManagerEnabled && !!googleTagManagerId
        ? ScreenService.addHtmlScript(STATIC_FILE_PATH_SCRIPT_GOOGLE_TAG_MANAGER, false) : null;

      return (): void => {
        if (gtmHtmlElementScript) {
          ScreenService.removeHeadElement(gtmHtmlElementScript);
        }
      };
    }
  }, [isGtmClientInitialized]);

  useEffect((): void => {
    if (currentUserId) {
      async function runCheckIfPushNotificationsAllowed() {
        const pushNotificationsAllowed: boolean = await PushNotificationService.checkIfPushNotificationsAllowed();

        if (pushNotificationsAllowed) {
          enableMobilePushNotifications();
        }
      }

      runCheckIfPushNotificationsAllowed().then();
    }
  }, [currentUserId]);

  useEffect((): void => {
    if (currentPathname && currentPathname !== location.pathname) {
      setCurrentPathname(location.pathname);

      ScreenService.resetScrollPosition();
    }
  }, [currentPathname]);

  useEffect((): void => {
    if (googleTagManagerId && isGoogleTagManagerEnabled && !isGtmClientInitialized) {
      GoogleTagManagerService.init(googleTagManagerId);

      setIsGtmClientInitialized(true);
    }
  }, [googleTagManagerId]);

  useEffect((): void => {
    if (intercomAppId && isIntercomEnabled && !isIntercomClientInitialized
      && currentUser && currentUser.id && currentUser.email && currentUser.fullName
      && currentUser.createdDateValue && currentUser.intercomUserHash) {
      IntercomService.init(
        intercomAppId,
        currentUser.id,
        currentUser.email,
        currentUser.fullName,
        currentUser.createdDateValue,
        currentUser.intercomUserHash,
      );

      setIsIntercomClientInitialized(true);
    }
  }, [intercomAppId, currentUser]);

  useEffect((): void => {
    if (storedAuthToken && appAuthToken !== storedAuthToken) {
      setAppAuthToken(storedAuthToken);

      dispatch(getBaseData(getOptions(), undefined, (exception: ServerError): void => {
        if (exception && exception.statusCode === HTTP_CODE_UNAUTHORIZED) {
          onUserSessionExpired();
          dispatch(resetBaseData());

          navigate({
            pathname: ROUTE_LOGIN,
          });
        }
      }));
    }
  }, [storedAuthToken]);

  useEffect((): void => {
    if (currentUser && currentUser.id && isUserSessionExpired) {
      setIsUserSessionConfirmModalOpen(true);
    }
  }, [isUserSessionExpired, currentUser]);

  useEffect((): void => {
    if (isServerFault) {
      reloadApp();
    } else if (checkConnectionInterval) {
      clearInterval(checkConnectionInterval);

      setCheckConnectionInterval(null);
    }
  }, [isServerFault]);

  useEffect((): void => {
    if (isDataLoaded) {
      dispatch(getCommentListData(getOptions()));
      dispatch(getNotificationListData(getOptions()));
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (currentUserId && notificationActiveSubscription && commentActiveSubscription
      && userSessionExpiredActiveSubscription) {
      (async (): Promise<void> => {
        await notificationActiveSubscription.unsubscribe();
        await commentActiveSubscription.unsubscribe();
        await userSessionExpiredActiveSubscription.unsubscribe();
      })();

      ClientService.closeConnections();

      initSubscriptions();
    }
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  useEffect((): void => {
    if (isLoading && !storedAuthToken) {
      dispatch(resetAppLoader());
    }
  }, [isLoading]);

  useEffect((): void => {
    if (currentUserId) {
      initSubscriptions();
    }
  }, [currentUserId]);

  useEffect((): ReactUseEffectCleanUp => {
    return (): void => {
      if (oktaAuth && oktaAuth.options) {
        oktaAuth.options.restoreOriginalUri = undefined;
      }
    };
  }, []);

  useCapacitorAppUrlOpen();

  return (
    <ThemeProvider theme={MainTheme}>
      <GlobalStylesBlock isEditMode={isEditMode} />
      <DragAndDropProvider>
        {
          ((isLoading && storedAuthToken) || isServerFault) ? (
            <Loader />
          ) : (
            <FlexContainer isDefault>
              <SecurityLayout
                oktaAuth={oktaAuth}
                onAuthRequired={onOktaAuthRequired}
                onRestoreOriginalUri={oktaRestoreOriginalUri}
              >
                <Header />
                <Navigation />
                <MainContainer>
                  <Outlet />
                </MainContainer>
                <RightDrawer />
              </SecurityLayout>
              <ConfirmModal
                isOpen={isUserSessionConfirmModalOpen}
                title={userSessionConfirmModalTitle}
                text={userSessionConfirmModalText}
                confirmText={userSessionConfirmModalConfirmText}
                onConfirm={onUserSessionCloseConfirm}
                onCloseModal={closeConfirmModal}
              />
            </FlexContainer>
          )
        }
      </DragAndDropProvider>
    </ThemeProvider>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    portfolioFilter,
    notifications,
    comments,
  } = reducers;

  return {
    isLoading: app.isLoading,
    isServerFault: app.isServerFault,
    isUserSessionExpired: app.isUserSessionExpired,
    isDataLoaded: !!app.baseData,
    currentUser: app.baseData && app.baseData.currentUser,
    isEditMode: controls.isEditMode,
    notificationActiveSubscription: notifications.notificationActiveSubscription,
    commentActiveSubscription: comments.commentActiveSubscription,
    userSessionExpiredActiveSubscription: app.userSessionExpiredActiveSubscription,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
  };
};

export default connect(mapState)(App);
